"use client"

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Loader2, Search, X } from "lucide-react"
import { useAuth } from "@/hooks/use-auth"
import type { ProductOption } from "@/types/shopify"
import { buildOrdersPageHref } from "./utils"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"





export interface ProductFilterPanelHandle {
  focusSearch: () => void
}

interface ProductFilterPanelProps {
  integrationId: string
  dateRange: { month_start_utc: Date; month_end_utc: Date }
  minimumOrderTotal: number
  selectedProducts: Map<number, ProductOption>
  onSelectionChange: (products: Map<number, ProductOption>) => void
  onClose: () => void
}

export const ProductFilterPanel = forwardRef<ProductFilterPanelHandle, ProductFilterPanelProps>(
  function ProductFilterPanel({ integrationId, dateRange, minimumOrderTotal, selectedProducts, onSelectionChange, onClose }, ref) {


  const tUi = useTranslations("Shopify.ShopifyAttributionDashboardProductFilterPanel")

  const { fetchWithAuth, isAuthenticated } = useAuth()
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<ProductOption[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  useImperativeHandle(ref, () => ({
    focusSearch: () => searchInputRef.current?.focus(),
  }))

  // --- Search ---

  const searchProducts = useCallback(
    async (search: string) => {
      if (!integrationId || !isAuthenticated) return
      if (search.length < 2) {
        setResults([])
        return
      }

      setIsSearching(true)
      try {
        const params = new URLSearchParams({
          integration_id: integrationId,
          search,
          month_start_utc: dateRange.month_start_utc.toISOString(),
          month_end_utc: dateRange.month_end_utc.toISOString(),
        })
        if (minimumOrderTotal > 0) {
          params.set("minimum_order_total", String(minimumOrderTotal))
        }
        const res = await fetchWithAuth(`/api/integrations/shopify/products?${params}`)
        if (res.ok) {
          const data = await res.json()
          setResults(data.products ?? [])
        }
      } catch (err) {
        console.error("[ProductFilterPanel] Search error:", err)
      } finally {
        setIsSearching(false)
      }
    },
    [integrationId, isAuthenticated, fetchWithAuth, dateRange, minimumOrderTotal],
  )

  const handleQueryChange = (value: string) => {
    setQuery(value)

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      void searchProducts(value.trim())
    }, 300)
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  // Reset search when integration changes
  useEffect(() => {
    setQuery("")
    setResults([])
  }, [integrationId])

  // Re-run search when month/timezone/minimumOrderTotal changes (order counts depend on these)
  useEffect(() => {
    const trimmed = query.trim()
    if (trimmed.length >= 2) {
      void searchProducts(trimmed)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-search on filter params change only
  }, [dateRange, minimumOrderTotal])

  const selectedArray = useMemo(() => Array.from(selectedProducts.values()), [selectedProducts])

  // --- Actions ---

  const toggleProduct = (product: ProductOption) => {
    const next = new Map(selectedProducts)
    if (next.has(product.product_id)) {
      next.delete(product.product_id)
    } else {
      next.set(product.product_id, product)
    }
    onSelectionChange(next)
  }

  const removeProduct = (productId: number) => {
    const next = new Map(selectedProducts)
    next.delete(productId)
    onSelectionChange(next)
  }

  const clearAll = () => {
    onSelectionChange(new Map())
  }

  // Select All toggle
  const allResultsSelected = results.length > 0 && results.every(p => selectedProducts.has(p.product_id))

  const toggleSelectAll = () => {
    const next = new Map(selectedProducts)
    if (allResultsSelected) {
      for (const p of results) next.delete(p.product_id)
    } else {
      for (const p of results) next.set(p.product_id, p)
    }
    onSelectionChange(next)
  }

  // --- Helpers for product chip rendering ---

  const renderProductChip = (product: ProductOption) => {
    const sku = product.sku || undefined
    const subtitle = [sku ? `SKU: ${sku}` : null, product.variant_title].filter(Boolean).join(" · ")

    return (
      <Tooltip key={product.product_id}>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2 rounded-md bg-secondary px-2 py-1.5">
            <a
              href={buildOrdersPageHref(integrationId, dateRange, [product.product_id])}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="shrink-0 text-[11px] font-semibold tabular-nums text-muted-foreground underline decoration-dotted cursor-pointer hover:text-foreground"
            >
              {product.order_count}
            </a>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium leading-tight">{product.title}</p>
              {subtitle && (
                <p className="truncate text-[10px] leading-tight text-muted-foreground">{subtitle}</p>
              )}
            </div>
            <button
              type="button"
              className="shrink-0 rounded-full p-0.5 hover:bg-muted"
              onClick={() => removeProduct(product.product_id)}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </TooltipTrigger>
        <TooltipContent side="left">
          <p className="text-xs font-medium">{product.title}</p>
          {subtitle && (
            <p className="text-[10px] text-muted-foreground">{subtitle}</p>
          )}
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <div className="flex h-full flex-col bg-background">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold"><span key={tUi("productFilter")}>{tUi("productFilter")}</span></h3>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Search */}
        <div className="border-b px-4 py-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              placeholder={tUi("searchByNameSKUOrVariant")}
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              className="pl-9"
            />
          </div>
          {query.length > 0 && query.trim().length < 2 && (
            <p className="mt-1.5 text-xs text-muted-foreground"><span key={tUi("typeAtLeast2CharactersToSearch")}>{tUi("typeAtLeast2CharactersToSearch")}</span></p>
          )}
        </div>

        {/* Selected products */}
        {selectedArray.length > 0 && (
          <div className="border-b px-4 py-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground"><TranslationText>{tUi.rich("selectedValue0", { value0: String(selectedArray.length) })}</TranslationText></span>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={clearAll}>
                <span key={tUi("clearAll")}>{tUi("clearAll")}</span></Button>
            </div>
            <TooltipProvider delayDuration={300}>
              <div className="flex flex-col gap-1.5">
                {selectedArray.map(renderProductChip)}
              </div>
            </TooltipProvider>
          </div>
        )}

        {/* Results */}
        <ScrollArea className="flex-1">
          <div className="px-4 py-2">
            {isSearching && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}

            {!isSearching && query.trim().length >= 2 && results.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground"><span key={tUi("noProductsFound")}>{tUi("noProductsFound")}</span></p>
            )}

            {!isSearching && query.trim().length < 2 && results.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                <span key={tUi("startTypingToSearchProducts")}>{tUi("startTypingToSearchProducts")}</span></p>
            )}

            {/* Results header with Select All */}
            {!isSearching && results.length > 0 && (
              <>
                <div className="flex items-center justify-between px-2 py-1.5">
                  <span className="text-xs font-medium text-muted-foreground"><TranslationText>{tUi.rich("resultsValue0", { value0: String(results.length) })}</TranslationText></span>
                </div>
                <label
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50 border-b mb-1"
                  onClick={toggleSelectAll}
                >
                  <Checkbox
                    checked={allResultsSelected}
                    className="h-3.5 w-3.5"
                    tabIndex={-1}
                  />
                  <span key={allResultsSelected ? tUi("deselectAllValue0", { value0: String(results.length) }) : tUi("selectAllValue0", { value0: String(results.length) })} className="text-xs font-medium text-muted-foreground">
                    {allResultsSelected ? tUi("deselectAllValue0", { value0: String(results.length) }) : tUi("selectAllValue0", { value0: String(results.length) })}
                  </span>
                </label>
              </>
            )}

            {!isSearching && (
              <TooltipProvider delayDuration={300}>
                {results.map((product) => {
                  const isSelected = selectedProducts.has(product.product_id)
                  const subtitle = [product.sku ? `SKU: ${product.sku}` : null, product.variant_title].filter(Boolean).join(" · ")

                  return (
                    <Tooltip key={product.product_id}>
                      <TooltipTrigger asChild>
                        <label
                          className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50"
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleProduct(product)}
                          />
                          <span className="shrink-0 text-[11px] font-semibold tabular-nums text-muted-foreground">
                            {product.order_count}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-medium leading-tight">{product.title}</p>
                            {subtitle && (
                              <p className="truncate text-[10px] leading-tight text-muted-foreground">{subtitle}</p>
                            )}
                          </div>
                        </label>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <p className="text-xs font-medium">{product.title}</p>
                        {subtitle && (
                          <p className="text-[10px] text-muted-foreground">{subtitle}</p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  )
                })}
              </TooltipProvider>
            )}
          </div>
        </ScrollArea>
    </div>
  )
})
